
import React, { useState } from 'react';
import { Plus, Minus, ArrowRight } from 'lucide-react';

interface FAQItemProps {
  question: string;
  answer: React.ReactNode;
}

const FAQItem = ({ question, answer }: FAQItemProps) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className="border-b border-zodiac/10 group">
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="w-full py-10 flex justify-between items-center text-left transition-all"
      >
        <span className={`text-xl md:text-2xl font-bold tracking-tight transition-colors duration-300 ${isOpen ? 'text-accent' : 'text-zodiac group-hover:text-accent/70'}`}>
          {question}
        </span>
        <div className={`shrink-0 ml-8 w-10 h-10 rounded-full border flex items-center justify-center transition-all duration-500 ${isOpen ? 'bg-accent border-accent text-white rotate-180' : 'border-zodiac/10 text-zodiac'}`}>
          {isOpen ? <Minus className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
        </div>
      </button>
      <div className={`overflow-hidden transition-all duration-500 ease-in-out ${isOpen ? 'max-h-[800px] pb-12 opacity-100' : 'max-h-0 opacity-0'}`}>
        <div className="text-zodiac/60 leading-relaxed font-light text-lg md:text-xl max-w-3xl">
          {answer}
        </div>
      </div>
    </div>
  );
};

interface FAQProps {
  onNavigate?: (view: 'home' | 'pilot') => void;
}

export const FAQ: React.FC<FAQProps> = ({ onNavigate }) => {
  return (
    <section id="faq" className="py-40 px-6 md:px-12 bg-brandBg border-t border-zodiac/5">
      <div className="max-w-5xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-24 gap-8">
          <div className="max-w-2xl">
            <p className="text-[10px] font-black text-accent uppercase tracking-[0.5em] mb-6">OPERATIONAL CLARITY</p>
            <h2 className="text-4xl md:text-6xl font-bold tracking-tighter text-zodiac leading-[0.9]">
              Common Questions.
            </h2>
          </div>
          <p className="text-lg text-zodiac/40 font-light max-w-xs">
            Everything you need to know about piloting and scaling Groflex.
          </p>
        </div>

        <div className="space-y-0">
          <FAQItem 
            question="1. How can I trial Groflex?" 
            answer={
              <div className="space-y-4">
                <p>Groflex offers a trial period of <strong>up to 4 weeks</strong> for mid-market teams and operational leaders who want to evaluate the platform on real workflows before committing.</p>
                <p>The trial provides controlled, time-bound access to explore how Groflex connects signals, decisions, and execution across existing systems. It is self-service, with optional support available if required.</p>
                <p>At the end of the trial, teams can either move forward to a self-service Growth Plan or explore a paid Enterprise Pilot, depending on scale and operational complexity.</p>
              </div>
            } 
          />
          <FAQItem 
            question="2. How does the Enterprise Pilot work?" 
            answer={
              <div className="space-y-4">
                <p>The Enterprise Pilot is a consultative, outcome-driven engagement designed for larger or more complex organisations.</p>
                <p>It runs for <strong>up to 12 weeks</strong> and focuses on validating Groflex under real operational conditions, including governance, coordination, and execution across multiple systems.</p>
                <p>Enterprise Pilots are scoped collaboratively and tailored to the organisation’s structure, systems, and objectives.</p>
                <button 
                  onClick={() => onNavigate?.('pilot')}
                  className="mt-4 flex items-center gap-2 text-accent font-bold text-base hover:opacity-70 transition-opacity"
                >
                  Learn more about the Enterprise Pilot
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            } 
          />
          <FAQItem 
            question="3. Are Enterprise Pilots paid?" 
            answer={
              <div className="space-y-4">
                <p><strong>Yes. Enterprise Pilots are paid engagements.</strong></p>
                <p>They are structured, time-bound, and outcome-driven programs designed to validate Groflex in real operational environments. Pricing reflects the scope, systems involved, and level of collaboration required.</p>
                <p>This ensures:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Dedicated support and accountability</li>
                  <li>Proper scoping and governance</li>
                  <li>Serious commitment from both sides</li>
                </ul>
                <p>Enterprise Pilots are not demos. They are validation programs that inform long-term deployment decisions.</p>
              </div>
            } 
          />
          <FAQItem 
            question="4. What are your security and deployment standards?" 
            answer={
              <div className="space-y-4">
                <p>Groflex is built with enterprise security and compliance principles in mind:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Secure data handling and access controls</li>
                  <li>Role-based permissions (RBAC)</li>
                  <li>Deployment aligned with enterprise IT and security requirements</li>
                  <li>No forced data migration—Groflex connects to existing systems</li>
                </ul>
                <p className="text-base italic">Specific security, hosting, and deployment details are shared during the trial or pilot onboarding process.</p>
              </div>
            } 
          />
          <FAQItem 
            question="5. How does pricing work?" 
            answer={
              <div className="space-y-4">
                <p>Groflex pricing is customised based on scale, complexity, and usage. Pricing is discussed once value and fit are validated.</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Trial Access:</strong> May be granted to allow teams to view and explore the platform.</li>
                  <li><strong>Growth Plan:</strong> Self-service, designed for mid-market teams validating execution coordination.</li>
                  <li><strong>Enterprise Plan:</strong> Custom-priced, consultative, and aligned to enterprise scope and governance needs.</li>
                </ul>
              </div>
            } 
          />
        </div>
      </div>
    </section>
  );
};
